import { TabContext, TabList, TabPanel } from '@mui/lab'
import { Divider, Stack, Tab, Typography } from '@mui/material'
import { TablePagination } from 'components/TablePagination'
import PageWrapper from 'hoc/PageWrapper'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryState } from 'react-router-use-location-state'
import { CommissionEvaluationItem } from './commission-evaluation-item'
import { useCommissionEvaluationOverviewQuery } from 'services/commission-evaluation/commission-evaluation'
import { ECommissionEvaluationStatus } from 'types/commission-evaluation/commission-evaluation'
import { Loading } from 'components/Loading'
import { Error } from 'components/Error'

export const TenderCommittee = () => {
  const [tab, setTab] = useState<ECommissionEvaluationStatus>(
    ECommissionEvaluationStatus.PENDING
  )
  const { t } = useTranslation()
  const [page, setPage] = useQueryState('page', 0)

  const rowsPerPage = 10

  const args = {
    page,
    size: rowsPerPage,
    status: tab as ECommissionEvaluationStatus,
  } as const

  const {
    data: commissionEvaluations,
    isError,
    isLoading,
  } = useCommissionEvaluationOverviewQuery(args, { refetchOnMountOrArgChange: true })

  const tenderTabs = useMemo(() => {
    return [
      {
        value: ECommissionEvaluationStatus.PENDING,
        label: t('documents-tab'),
      },
      {
        value: ECommissionEvaluationStatus.ACCEPTED,
        label: t('confirmed-documents-tab'),
      },
      {
        value: ECommissionEvaluationStatus.REJECTED,
        label: t('rejected-documents-tab'),
      },
    ]
  }, [t])

  if (isLoading) return <Loading fullPage={false} />
  if (isError) return <Error fullPage={false} />

  return (
    <PageWrapper>
      <Stack spacing={2}>
        <TabContext value={tab}>
          <TabList
            onChange={(_event, newValue) => {
              setTab(newValue)
              setPage(0)
            }}
            sx={{
              '&& .MuiTabs-flexContainer': {
                justifyContent: 'center',
              },
            }}
          >
            {tenderTabs.map(({ value, label }) => (
              <Tab
                sx={{ background: '#FFF' }}
                key={value}
                label={label}
                value={value}
              />
            ))}
          </TabList>
          <Divider />
          <Typography variant="h6">
            {tab === ECommissionEvaluationStatus.PENDING
              ? t('documents-tab')
              : tab === ECommissionEvaluationStatus.ACCEPTED
                ? t('confirmed-documents-tab')
                : t('rejected-documents-tab')}
          </Typography>
          {commissionEvaluations && (
            <>
              <TabPanel value={ECommissionEvaluationStatus.PENDING}>
                <CommissionEvaluationItem
                  commissionEvaluations={commissionEvaluations?.items}
                  status={ECommissionEvaluationStatus.PENDING}
                />
              </TabPanel>
              <TabPanel value={ECommissionEvaluationStatus.ACCEPTED}>
                <CommissionEvaluationItem
                  commissionEvaluations={commissionEvaluations?.items}
                  status={ECommissionEvaluationStatus.ACCEPTED}
                />
              </TabPanel>
              <TabPanel value={ECommissionEvaluationStatus.REJECTED}>
                <CommissionEvaluationItem
                  commissionEvaluations={commissionEvaluations?.items}
                  status={ECommissionEvaluationStatus.REJECTED}
                />
              </TabPanel>
            </>
          )}
        </TabContext>
        {commissionEvaluations && commissionEvaluations.info.totalRows !== 0 && (
          <TablePagination
            total={commissionEvaluations.info.totalRows}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={setPage}
          />
        )}
      </Stack>
    </PageWrapper>
  )
}
